import React, { useState } from 'react';
import { Lock, Mail, KeyRound, ArrowRight, AlertCircle, RefreshCw, Hash } from 'lucide-react';
import { signIn, completeNewPassword, signUp, confirmSignUp, resendConfirmationCode } from './auth';

// ─────────────────────────────────────────────────────────────
// Login screen — AWS Cognito (SRP) sign-in.
// Calls onAuthenticated() once a valid session is established.
// ─────────────────────────────────────────────────────────────
export default function Login({ onAuthenticated }) {
  // 'login'      → email + password
  // 'register'   → self-service sign-up (email + password)
  // 'confirm'    → enter the emailed confirmation code
  // 'newPassword'→ admin-created account must choose a new password
  const [stage, setStage]       = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [newPass, setNewPass]   = useState('');
  const [confirm, setConfirm]   = useState('');
  const [code, setCode]         = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const [notice, setNotice]     = useState(null);

  const switchStage = (next) => {
    setStage(next);
    setError(null);
    setNotice(null);
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await signIn(email.trim(), password);
      if (res.status === 'NEW_PASSWORD_REQUIRED') {
        setStage('newPassword');
      } else {
        onAuthenticated();
      }
    } catch (err) {
      setError(err.message || 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (newPass !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await completeNewPassword(newPass);
      onAuthenticated();
    } catch (err) {
      setError(err.message || 'Could not set new password');
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (newPass !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await signUp(email.trim(), newPass);
      // Keep the chosen password so we can auto sign-in after confirmation.
      setPassword(newPass);
      if (res.userConfirmed) {
        // No email verification configured — sign straight in.
        await signIn(email.trim(), newPass);
        onAuthenticated();
      } else {
        switchStage('confirm');
        setNotice(`We emailed a confirmation code to ${email.trim()}`);
      }
    } catch (err) {
      setError(err.message || 'Could not create account');
    } finally {
      setBusy(false);
    }
  };

  const submitConfirm = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await confirmSignUp(email.trim(), code.trim());
      // Auto sign-in if we still hold the password from registration.
      if (password) {
        await signIn(email.trim(), password);
        onAuthenticated();
      } else {
        switchStage('login');
        setNotice('Account confirmed — you can sign in now');
      }
    } catch (err) {
      setError(err.message || 'Could not confirm account');
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await resendConfirmationCode(email.trim());
      setNotice('A new confirmation code is on its way');
    } catch (err) {
      setError(err.message || 'Could not resend code');
    } finally {
      setBusy(false);
    }
  };

  const field = {
    width: '100%', padding: '12px 14px 12px 42px',
    background: 'var(--surface-2)', border: '1px solid var(--line-2)',
    borderRadius: 12, fontFamily: 'Space Grotesk,sans-serif', fontSize: 14,
    color: 'var(--text)', outline: 'none',
  };
  const iconWrap = { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' };
  const labelStyle = { fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6, display: 'block' };

  const submitBtn = (label) => (
    <button type="submit" disabled={busy} style={{
      width: '100%', marginTop: 8, padding: '13px 16px',
      background: busy ? 'var(--text-dim)' : 'var(--text)', color: 'var(--bg)',
      border: 'none', borderRadius: 12, cursor: busy ? 'wait' : 'pointer',
      fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      transition: 'all 0.2s',
    }}>
      {busy
        ? <><RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />Please wait…</>
        : <>{label}<ArrowRight size={15} /></>}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Inter,system-ui,sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        :root{
          --bg:#f5f3f0; --surface:#fff; --surface-2:#fafaf9;
          --line:rgba(0,0,0,0.08); --line-2:rgba(0,0,0,0.12);
          --text:#1a1a1a; --text-mid:#525252; --text-dim:#737373;
          --accent:#00d9ff; --accent-dim:#00b8d4; --accent-dark:#0891b2;
        }
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.8)}}
        input:focus{border-color:var(--accent)!important}
        body{background:var(--bg)}
      `}</style>

      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Lock size={20} strokeWidth={2.2} style={{ color: 'var(--accent)' }} />
            </div>
            <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 30, letterSpacing: '0.06em' }}>SLANT</div>
          </div>
          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: 'var(--text-dim)', letterSpacing: '0.1em' }}>
            {{
              login:       'Sign in to your Conik M1',
              register:    'Create your Conik M1 account',
              confirm:     'Confirm your email address',
              newPassword: 'Choose a new password',
            }[stage]}
          </div>
        </div>

        {/* Card */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 24, padding: '28px 24px', boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 12, marginBottom: 18 }}>
              <AlertCircle size={15} color="#ef4444" style={{ flexShrink: 0 }} />
              <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: '#ef4444' }}>{error}</span>
            </div>
          )}

          {notice && !error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(0,217,255,0.08)', border: '1px solid rgba(0,217,255,0.3)', borderRadius: 12, marginBottom: 18 }}>
              <Mail size={15} color="#0891b2" style={{ flexShrink: 0 }} />
              <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: '#0891b2' }}>{notice}</span>
            </div>
          )}

          {stage === 'login' ? (
            <form onSubmit={submitLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} style={iconWrap} />
                  <input type="email" autoComplete="username" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                    required style={field} />
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={iconWrap} />
                  <input type="password" autoComplete="current-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                    required style={field} />
                </div>
              </div>
              {submitBtn('Sign in')}
            </form>
          ) : stage === 'register' ? (
            <form onSubmit={submitRegister}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} style={iconWrap} />
                  <input type="email" autoComplete="username" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                    required style={field} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={iconWrap} />
                  <input type="password" autoComplete="new-password" value={newPass}
                    onChange={(e) => setNewPass(e.target.value)} placeholder="Choose a password"
                    required style={field} />
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={labelStyle}>Confirm password</label>
                <div style={{ position: 'relative' }}>
                  <KeyRound size={16} style={iconWrap} />
                  <input type="password" autoComplete="new-password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password"
                    required style={field} />
                </div>
              </div>
              {submitBtn('Create account')}
            </form>
          ) : stage === 'confirm' ? (
            <form onSubmit={submitConfirm}>
              <div style={{ marginBottom: 8 }}>
                <label style={labelStyle}>Confirmation code</label>
                <div style={{ position: 'relative' }}>
                  <Hash size={16} style={iconWrap} />
                  <input type="text" inputMode="numeric" autoComplete="one-time-code" value={code}
                    onChange={(e) => setCode(e.target.value)} placeholder="123456"
                    required style={field} />
                </div>
              </div>
              {submitBtn('Confirm & continue')}
              <button type="button" onClick={resendCode} disabled={busy} style={{
                width: '100%', marginTop: 10, padding: '10px', background: 'none',
                border: 'none', cursor: busy ? 'wait' : 'pointer',
                fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: 'var(--text-mid)',
              }}>
                Didn’t get a code? Resend
              </button>
            </form>
          ) : (
            <form onSubmit={submitNewPassword}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>New password</label>
                <div style={{ position: 'relative' }}>
                  <KeyRound size={16} style={iconWrap} />
                  <input type="password" autoComplete="new-password" value={newPass}
                    onChange={(e) => setNewPass(e.target.value)} placeholder="New password"
                    required style={field} />
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={labelStyle}>Confirm password</label>
                <div style={{ position: 'relative' }}>
                  <KeyRound size={16} style={iconWrap} />
                  <input type="password" autoComplete="new-password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password"
                    required style={field} />
                </div>
              </div>
              {submitBtn('Set password & continue')}
            </form>
          )}
        </div>

        {(stage === 'login' || stage === 'register') && (
          <div style={{ textAlign: 'center', marginTop: 18, fontFamily: 'Space Grotesk,sans-serif', fontSize: 13, color: 'var(--text-mid)' }}>
            {stage === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button type="button" onClick={() => switchStage(stage === 'login' ? 'register' : 'login')} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: 'Space Grotesk,sans-serif', fontSize: 13, fontWeight: 600,
              color: 'var(--accent-dark)',
            }}>
              {stage === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 18, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.08em' }}>
          Secured by AWS Cognito
        </div>
      </div>
    </div>
  );
}
